import type { FaceRect, KnowledgeStatus, StreamTextToSpeechVoice } from '@d-id/types';

export enum AgentConvertStatus {
    V2Unconvertible = 'V2Unconvertible',
    V2Convertable = 'V2Convertable',
    V2Converted = 'V2Converted',
}

export enum AgentGrounding {
    Grounded = 'rag-grounded',
    Hybrid = 'rag-ungrounded',
    Ungrounded = 'assistant',
}

export enum AgentKnowledgeSource {
    BaseKnowledge = 'base_knowledge',
    Documents = 'documents',
}

export interface PromptCustomization {
    role?: string;
    personality?: string;
    topics_to_avoid?: string[];
    max_response_length?: number;
    knowledge_source?: AgentKnowledgeSource;
    prompt_knowledge?: string;
}

export type LLMProvider = 'openai' | 'antropic' | 'external-openai';

export enum AgentUseCase {
    CAMPAIGNS = 'd-id-campaigns',
    AGENTS = 'd-id-agents',
}

export enum LLM_OPTIONS {
    GPT_35_TURBO = 'gpt-35-turbo',
    GPT_4O_GLOBAL = 'gpt-4o-global',
    GPT_4O_MINI = 'gpt-4o-mini',
    GPT_41 = 'gpt-4.1',
    GPT_41_MINI = 'gpt-4.1-mini',
    GPT_41_NANO = 'gpt-4.1-nano',
    EXTERNAL_OPENAI = 'external-openai',
}

export interface BaseLLM {
    provider: LLMProvider;
    instructions?: string;
    config?: {
        assistant_id?: string;
    };
    template?: AgentGrounding;
    prompt_customization?: PromptCustomization;
    temperature?: number;
    model: LLM_OPTIONS;
}

export interface OpenAIExternalLLM extends Omit<BaseLLM, 'model'> {
    provider: 'external-openai';
    model: string;
    secretId: string;
    endpoint: string;
    isStreaming: boolean;
    maxMessages?: number;
}

export type LLM = BaseLLM | OpenAIExternalLLM;

export interface IAgent extends IAgentData {
    created_at: string;
    modified_at: string;
    owner_id?: string;
    status: AgentStatus;
    chats?: string;
    score?: string;
    id: string;
    conversion?: AgentConvertStatus;
    from_template?: string;
}

export interface IAgentData {
    preview_name: string;
    preview_thumbnail?: string;
    presenter?: Partial<TalkPresenter> | Partial<ClipPresenter>;
    knowledge?: Knowledge;
    use_case?: AgentUseCase;
    tags?: string[];
    access?: string;
    starter_message?: string[];
    embed?: boolean;
    greetings?: string[];
    llm?: LLM;
    status?: AgentStatus;
    version?: number;
    triggers: Triggers | undefined;
}

/**
 * This is what the API expects to receive when updating an agent
 * More fields may probably be omitted - the API does'nt expect lots of data
 * The real type is very close to DeepPartial<IAgentData>, but not exactly, so we can't just do that
 */
export type AgentUpdateDTO = Partial<Omit<IAgentData, 'llm'>> & { llm?: Partial<LLM> };

export interface BasePresenter {
    type: 'talk' | 'clip';
    voice?: StreamTextToSpeechVoice;
    idle_video?: string;
    thumbnail: string;
    background?: {
        source_url?: string;
        color?: string;
    };
}

export interface TalkPresenter extends BasePresenter {
    type: 'talk';
    source_url: string;
    driver_url?: string;
    stitch?: boolean;
    face?: FaceRect[];
}

export interface ClipPresenter extends BasePresenter {
    type: 'clip';
    source_url: string;
    presenter_id: string;
    driver_id?: string;
}

export interface KnowledgeEmbedder {
    provider: string;
    model: string;
}

export interface Knowledge {
    id: string;
    provider: 'pinecone' | 'redis';
    embedder?: KnowledgeEmbedder;
    status?: KnowledgeStatus;
    starter_message?: string[];
    base_knowledge?: string;
}

export enum AgentStatus {
    Created = 'created',
    Started = 'started',
    Done = 'done',
    Error = 'error',
    Rejected = 'rejected',
    Ready = 'ready',
}

export enum AgentCreateTabType {
    Appearance = 'Appearance',
    Details = 'Details',
    KnowledgeBase = 'KnowledgeBase',
    ChatSettings = 'ChatSettings',
}

export interface IAgentKey {
    client_key?: string;
    use_case?: string;
    allowed_domains?: string[];
    chat_sessions?: Number;
    expire_at?: string;
}

export enum QuestionsOperationType {
    Create = 'Create',
    Edit = 'Edit',
    Delete = 'Delete',
}

export enum TriggerType {
    chatEnd = 'chat/end',
    chatStart = 'chat/start',
}

export interface WebhookAuth {
    type: 'bearer' | 'api_key' | 'basic' | 'none';
    header_name?: string;
    secret_id?: string;
    token?: string;
    api_key?: string;
    username?: string;
    password?: string;
}

export interface TriggerWebhook {
    url: string;
    description?: string;
    headers?: Record<string, string>;
    auth?: WebhookAuth;
}

export interface TriggerTool {
    tool_id: string;
}

export interface Trigger {
    webhooks?: TriggerWebhook[];
    tools?: TriggerTool[];
    prompt_instructions?: string;
    tool_choice?: 'auto' | 'required';
}

export type Triggers = Partial<Record<TriggerType, Trigger>>;
